import React, { useEffect } from 'react';
import { Grid, Paper } from '@mui/material';
import { Container } from '@mui/system';
import MyCustomImageUploader from '../../../../components/imageInput/MyCustomImageUploader';
import { useState } from 'react';
import { useCategories } from '../../../../hooks/product/useCategories';
import { useLanguages } from '../../../../hooks/product/useLangauges';
import { useTaxTypes } from '../../../../hooks/product/useTaxTypes';
import ProductNames from './ProductNames';
import { TranslationInput } from '../../../../config/types/custom/TranslationInput';
import ProductDescription from './ProductDescription';
import ProductIds from './ProductIds';
import Shippings from './Shippings';
import Categories from './Categories';
import { CategoryModel } from '../../../../config/types/models/CategoryModel';
import Tags from './Tags';
import BooleansInfo from './BooleansInfo';
import ImagesInput from './ImagesInput';
import { Form, useFormik } from 'formik';

import {
  useProductDetails,
  useSaveProduct,
} from '../../../../hooks/product/useProductDetails';
import { Loading, LoadingPage, useNotify, useTranslate } from 'react-admin';
import { useAwsUpload } from '../../../../dataProviders/useAwsUpload';
import AttachmentsInput from './AttachmentsInput';
import ProductFunctionsBar from './ProductFunctionsBar';

type IProps = {
  id?: number | string;
};
function ChangeProduct({ id }: IProps) {
  // states
  const { loading, upload } = useAwsUpload();
  const [name, setName] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [reference, setReference] = useState<string>('');
  const [barcode, setBarcode] = useState<string>('');
  const [sku, setSku] = useState<string>('');
  const [price, setPrice] = useState<number>(0);
  const [oldPrice, setOldPrice] = useState<number>(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [unit, setUnit] = useState<string>('');
  const [weight, setWeight] = useState<number>(0);
  const [length, setLength] = useState<number>(0);
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const [image, setImage] = useState<string>('');
  const [dbImage, setDbImage] = useState<string>('');
  const [images, setImages] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [categories, setCategories] = useState<CategoryModel[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [taxType, setTaxType] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isFeatured, setIsFeatured] = useState<boolean>(false);
  const [translations, setTranslations] = useState<TranslationInput[]>([]);

  // hooks
  const notify = useNotify();
  const t = useTranslate();
  const { data: allCategories, isLoading: loadingCategories } = useCategories();
  const { data: langauges, isLoading: loadingLanguages } = useLanguages();
  const { data: taxTypes, isLoading: loadingTaxTypes } = useTaxTypes();
  const saveProduct = useSaveProduct();

  const { data: currentProduct, isLoading: isLoadingProductDetails } =
    useProductDetails(id);

  // functions
  const onChangeImage = async (f: File[], b: string[]) => {
    if (f.length > 0) {
      const newImageUrl = await upload(f[0]);
      setImage(newImageUrl);
    } else {
      setImage(dbImage);
    }
  };

  const onChangeImages = async (f: File[], a: string[]) => {
    if (f.length > 0) {
      const newImageUrl = await upload(f[0]);

      setImages([...images, newImageUrl]);
    }
  };

  const onChangeAttachments = async (f: any) => {
    if (f.length > 0) {
      const newImageUrl = await upload(f[0]);
      setAttachments([...attachments, newImageUrl]);
    }
  };

  const onRemoveAttachment = (att: string) => {
    const newAttachments = attachments.filter((a) => a !== att);
    setAttachments(newAttachments);
  };

  useEffect(() => {
    if (currentProduct) {
      setName(currentProduct.name);
      setSlug(currentProduct.slug);
      setDescription(currentProduct.description);
      currentProduct.reference && setReference(currentProduct.reference);
      setSku(currentProduct.sku);
      currentProduct.price && setPrice(currentProduct.price);
      currentProduct.oldPrice && setOldPrice(currentProduct.oldPrice);
      currentProduct.quantity && setQuantity(currentProduct.quantity);
      currentProduct.unit && setUnit(currentProduct.unit);
      currentProduct.weight && setWeight(currentProduct.weight);
      currentProduct.length && setLength(currentProduct.length);
      currentProduct.width && setWidth(currentProduct.width);
      currentProduct.height && setHeight(currentProduct.height);
      currentProduct.image && setImage(currentProduct.image);
      currentProduct.image && setDbImage(currentProduct.image);
      currentProduct.images && setImages(currentProduct.images);
      currentProduct.attachments && setAttachments(currentProduct.attachments);
      currentProduct.categories && setCategories(currentProduct.categories);
      currentProduct.tags && setTags(currentProduct.tags);
      currentProduct.taxType && setTaxType(currentProduct.taxType.id);
      currentProduct.isActive && setIsActive(currentProduct.isActive);
      currentProduct.isFeatured && setIsFeatured(currentProduct.isFeatured);
      currentProduct.translations &&
        setTranslations(currentProduct.translations);
    }
  }, [currentProduct]);

  const onSaveProduct = () => {
    saveProduct.mutate(
      {
        id: id ? parseInt(id.toString()) : null,
        name,
        slug,
        description,
        reference,
        sku,
        price,
        oldPrice,
        quantity,
        unit,
        weight,
        length,
        width,
        height,
        image,
        images,
        attachments,
        categories: categories.map((c) => c.id),
        tags,
        taxType,
        isActive,
        isFeatured,
        moms: 0,
        barcode,
        translations: translations.map((c) => ({
          key: c.key,
          languageId: c.languageId,
          value: c.value,
          productId: id ? parseInt(id.toString()) : null,
        })),
      },
      {
        onSuccess: () => {
          notify(t('productSaved'));
        },
      }
    );
  };

  if (
    isLoadingProductDetails ||
    loadingCategories ||
    loadingLanguages ||
    loadingTaxTypes
  ) {
    return <Loading />;
  }

  if (id && !currentProduct) return <LoadingPage />;

  return (
    <form>
      <Container sx={{ padding: 1 }}>
        <ProductFunctionsBar onSave={onSaveProduct} />
        <Grid container>
          <Grid padding={1} item lg={4} md={4} sm={12}>
            <Paper sx={{ padding: 2, height: 'calc(100% - 30px)' }}>
              <MyCustomImageUploader
                single={true}
                onChange={onChangeImage}
                image={image}
              />
            </Paper>
          </Grid>

          <Grid padding={1} item lg={4} md={4} sm={12}>
            <Paper sx={{ padding: 2, height: 'calc(100% - 30px)' }}>
              <ProductNames
                name={name}
                setName={setName}
                slug={slug}
                setSlug={setSlug}
                languages={langauges}
                translations={translations}
                setTranslations={setTranslations}
              />
              <BooleansInfo
                isActive={isActive}
                isFeatured={isFeatured}
                setIsActive={setIsActive}
                setIsFeatured={setIsFeatured}
                taxType={taxType}
                setTaxType={setTaxType}
                allTaxTypes={taxTypes}
              />
            </Paper>
          </Grid>
          <Grid padding={1} item lg={4} md={4} sm={12}>
            <Paper sx={{ padding: 2, height: 'calc(100% - 30px)' }}>
              <ProductIds
                sku={sku}
                setSku={setSku}
                reference={reference}
                setReference={setReference}
                price={price}
                setPrice={setPrice}
                id={id}
                oldPrice={oldPrice}
                setOldPrice={setOldPrice}
                barcode={barcode}
                setBarcode={setBarcode}
              />
            </Paper>
          </Grid>
        </Grid>

        <Grid container>
          <Grid padding={1} item lg={4} md={4} sm={12}>
            <Paper sx={{ padding: 2, height: 'calc(100% - 30px)' }}>
              <Shippings
                weight={weight}
                quantity={quantity}
                length={length}
                height={height}
                width={width}
                unit={unit}
                setWeight={setWeight}
                setQuantity={setQuantity}
                setLength={setLength}
                setHeight={setHeight}
                setWidth={setWidth}
                setUnit={setUnit}
              />
            </Paper>
          </Grid>
          <Grid padding={1} item lg={8} md={8} sm={12}>
            <Categories
              allCategories={allCategories}
              selected={categories}
              setCategories={setCategories}
            />
            <Tags tags={tags} setTags={setTags} />
          </Grid>
        </Grid>

        <Grid container>
          <Grid padding={1} item lg={12} md={12} sm={12}>
            <ImagesInput images={images} onChange={onChangeImages} />
          </Grid>
          <Grid padding={1} item lg={12} md={12} sm={12}>
            <AttachmentsInput
              attachments={attachments}
              onChange={onChangeAttachments}
              onRemove={onRemoveAttachment}
            />
          </Grid>
        </Grid>

        <Grid container>
          <Grid padding={1} item lg={12} md={12} sm={12}>
            <ProductDescription
              description={description}
              setDescription={setDescription}
              languages={langauges}
              translations={translations}
              setTranslations={setTranslations}
            />
          </Grid>
        </Grid>
      </Container>
    </form>
  );
}

export default ChangeProduct;
